import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { FilterSidebarProducts } from '@components/molecules/filters/FilterSidebarProducts';
import { ProductCardProps } from '@components/molecules/ProductCard';
import { ResultsContainer } from '@components/organisms/ResultsContainer';
import { StickyHeader } from '@components/molecules/StickyHeader/StickyHeader';
import { MarketplaceAdvertising } from '@components/molecules/MarketplaceAdvertising';
import { Footer } from '@components/molecules/Footer';
import { BreadcrumbsBanner } from '@components/molecules/BreadcrumbsBanner';
import { Space } from '@components/atoms/Space';
import { Container } from '@components/atoms/Container';
import { useInfiniteScroll } from '@hooks/useInfiniteScroll';
import { useFilters } from '@hooks/useFilters';
import { useData } from '@hooks/useData';
import { useCustomNavigate } from '@hooks/useCustomNavigate';
import { updateUrlSearchParams } from '@utils/search';
import { MenuPages, ResultsType } from '@types';

export const Products: FC = () => {
    const { navigate } = useCustomNavigate();
    const { products, isLoadingProducts } = useData();
    const { results } = useFilters();
    const { pageToRequest, checkPageToRequest, observerRef } = useInfiniteScroll();
    const { t } = useTranslation();

    const translations = {
        title: t('products-component.title'),
        orderNameAsc: t('sort-options.name_asc'),
        orderCreatedAtDesc: t('sort-options.created_at_desc'),
        orderPriceAsc: t('sort-options.price_asc'),
        orderPriceDesc: t('sort-options.price_desc'),
    };

    const handleOrderChange = (value: string) => {
        const updatedUrl = updateUrlSearchParams({ order: [value] });
        navigate(updatedUrl);
    };

    return (
        <>
            <StickyHeader menuActivePage={MenuPages.Products} />

            <Container>
                <BreadcrumbsBanner title={translations.title} links={[]} currentPage={translations.title} />
            </Container>

            <Space spaceBreakpoints={{ xs: { space: 50 } }} />

            <ResultsContainer
                type={ResultsType.products}
                results={results as ProductCardProps[]}
                scrollObserverRef={observerRef}
                filters={
                    <FilterSidebarProducts
                        isSearchResultsPage={false}
                        pageToRequest={pageToRequest}
                        checkPageToRequest={checkPageToRequest}
                    />
                }
                total={products?.pagination?.total || 0}
                order={{
                    options: [
                        { value: 'name_asc', text: translations.orderNameAsc },
                        { value: 'price_asc', text: translations.orderPriceAsc },
                        { value: 'price_desc', text: translations.orderPriceDesc },
                        { value: 'created_at_desc', text: translations.orderCreatedAtDesc },
                    ],
                    onChange: handleOrderChange,
                }}
                loading={isLoadingProducts}
            />

            <Space spaceBreakpoints={{ xs: { space: 50 }, md: { space: 100 } }} />

            <MarketplaceAdvertising />

            <Footer mode="normal" />
        </>
    );
};
